import React from 'react';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, LabelList,
} from 'recharts';

// =====================================================================
// Chamados — O PAINEL DE GESTÃO da aba Indicadores (Julia, 17/09/2026)
//
// *"A aba de Indicadores não me diz nada."* Ela respondia UMA pergunta — o % no
// prazo — e nenhuma das que a gestão faz: quantos chamados cada loja tem, o que
// está parado, o que mais quebra, para onde o atendimento escapa.
//
// ---------------------------------------------------------------------
// AS FORMAS, E POR QUE CADA UMA
// ---------------------------------------------------------------------
// ⚠️ A forma sai do TRABALHO do dado, não do gosto:
//   • **Retrato de agora → cartões, não gráfico.** São cinco números soltos que
//     se leem um a um; um gráfico aqui só põe eixo em volta de cinco dígitos.
//   • **Por loja → TABELA.** São 6 medidas por loja: gráfico de 6 séries vira
//     ilegível, e a comparação que a gestão faz é linha a linha.
//   • **O que mais quebra → barra horizontal.** Magnitude por categoria, com o
//     rótulo à esquerda (nome de família não cabe deitado num eixo X).
//   • **Prioridade → barra empilhada única.** É composição de um todo, e são 4
//     fatias — pizza de 4 fatias mente sobre proporção, barra não.
//
// ---------------------------------------------------------------------
// ⚠️⚠️ A COR: UM MATIZ SÓ, E ELE É O DO MÓDULO
// ---------------------------------------------------------------------
// A régua do projeto é "a tela usa a cor do módulo; varia o tom, nunca o matiz"
// (`docs/ARQUITETURA.md` › Paleta dos módulos). Prioridade é **ordinal**
// (Alta > Média > Baixa), então o certo é uma RAMPA de um matiz — do roxo escuro
// ao claro — e não quatro cores categóricas.
//
// ⚠️ A rampa foi VALIDADA, não escolhida no olho: a primeira tentativa
//    (`#581c87 · #7e22ce · #a855f7`) tinha ΔE de 13 entre vizinhos — abaixo do
//    piso de 15 para visão normal. Os passos foram afastados até passar
//    (ΔE 17–19, inclusive em protanopia e tritanopia).
// ⚠️ O tom mais claro fica abaixo de 3:1 contra o fundo, e isso OBRIGA rótulo
//    visível em cada fatia — que é o que está feito. Sem o número escrito, a
//    fatia clara seria invisível para quem enxerga pouco contraste.
// ⚠️ 'Sem prioridade' é CINZA de propósito: ele não é um degrau da escala, é a
//    ausência dela — pintá-lo de roxo o poria dentro de uma ordem que ele não tem.
// =====================================================================

const COR = 'var(--ch-cor)';
const COR_CLARA = 'var(--ch-cor-clara)';

// A rampa ordinal da prioridade (ver o cabeçalho: validada, não escolhida no olho).
// ⚠️ A rampa SEGUE A FILA (variáveis do `index.css`): na Manutenção o módulo é
//    índigo, e uma rampa roxa fixa aqui seria a única mancha de outro matiz na
//    tela inteira. As duas rampas foram validadas com os mesmos critérios.
const COR_PRIORIDADE = {
    Alta: 'var(--ch-prio-alta)',
    Media: 'var(--ch-prio-media)',
    Baixa: 'var(--ch-prio-baixa)',
    // ⚠️ Cinza nos dois matizes, de propósito: 'sem prioridade' não é um degrau
    //    da escala — é a ausência dela.
    semPrioridade: '#cbd5e1',
};
const ROTULO_PRIORIDADE = {
    Alta: 'Alta', Media: 'Média', Baixa: 'Baixa', semPrioridade: 'Sem prioridade',
};

const ROTULO_MOTIVO = {
    Invgate: 'Escalado ao Invgate',
    Pecas: 'Peça a comprar',
    Reparo: 'Peça em reparo',
};

const caixa = {
    backgroundColor: '#fff', border: `1px solid ${COR_CLARA}`,
    borderRadius: 12, padding: 18, marginBottom: 16,
};
const titulo = { margin: '0 0 2px 0', fontSize: 15, fontWeight: 800, color: '#0f172a' };
const subtitulo = { margin: '0 0 14px 0', fontSize: 12.5, color: '#64748b', lineHeight: 1.5 };
const th = {
    textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700,
    color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0',
    whiteSpace: 'nowrap',
};
// ⚠️ `tabular-nums` em toda coluna de número: sem isso os dígitos mudam de
//    largura e a coluna dança a cada atualização, o que atrapalha justamente a
//    leitura vertical que a tabela existe para permitir.
const td = {
    padding: '8px 10px', fontSize: 13, borderBottom: '1px solid #f1f5f9',
    fontVariantNumeric: 'tabular-nums',
};

/** Um número do retrato de agora. */
const Cartao = ({ rotulo, valor, cor, nota, destaque }) => (
    <div style={{
        flex: '1 1 150px', minWidth: 140, padding: 14, borderRadius: 10,
        border: `1px solid ${cor}22`, backgroundColor: `${cor}0d`,
    }}>
        <p style={{
            margin: 0, fontSize: 11, fontWeight: 700, color: '#64748b',
            textTransform: 'uppercase', letterSpacing: 0.3,
        }}>
            {rotulo}
        </p>
        <p style={{
            margin: '4px 0 0 0', fontSize: destaque ? 30 : 26, fontWeight: 800,
            color: cor, fontVariantNumeric: 'tabular-nums',
        }}>
            {valor}
        </p>
        {nota && <p style={{ margin: '2px 0 0 0', fontSize: 11.5, color: '#94a3b8' }}>{nota}</p>}
    </div>
);

const ChamadosPainelGestao = ({ painel, ehMobile }) => {
    if (!painel) return null;
    const { cartoes, porLoja, ranking, porPrioridade, porMotivoFinalizacao } = painel;

    const totalPrioridade = Object.values(porPrioridade || {}).reduce((a, b) => a + b, 0);
    const totalMotivos = Object.values(porMotivoFinalizacao || {}).reduce((a, b) => a + b, 0);

    // O ranking vira dado de gráfico. ⚠️ O rótulo junta família e loja: "POS" em
    // três lojas são três barras, e sem a loja no rótulo elas parecem repetidas.
    const dadosRanking = (ranking || []).map((r) => ({
        nome: r.semEquipamento ? `Sem equipamento · ${r.loja}` : `${r.familia} · ${r.loja}`,
        chamados: r.chamados,
        semEquipamento: r.semEquipamento,
    }));

    return (
        <>
            {/* ============ 1. O RETRATO DE AGORA ============ */}
            <div style={caixa}>
                <p style={titulo}>Onde a fila está agora</p>
                <p style={subtitulo}>
                    O estado dos chamados do período escolhido — é o que pede ação hoje.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    <Cartao rotulo="Total" valor={cartoes.total} cor="#475569" />
                    <Cartao rotulo="Em aberto" valor={cartoes.emAberto} cor="#b45309"
                        nota="aguardando triagem" />
                    <Cartao rotulo="Em andamento" valor={cartoes.emAndamento} cor="#3730a3"
                        nota="com o técnico" />
                    {/* ⚠️ O ATRASO É O NÚMERO DE AÇÃO desta tela, e por isso vem em
                        destaque e em vermelho: ele conta só o que está VIVO e já
                        estourou o prazo — o que terminou fora do prazo é passado, e
                        quem mede passado é o % no prazo, abaixo. */}
                    <Cartao rotulo="Em atraso" valor={cartoes.emAtraso} cor="#dc2626" destaque
                        nota="abertos e fora do prazo" />
                    <Cartao rotulo="Aguardando validação" valor={cartoes.aguardandoValidacao} cor="#0e7490"
                        nota="serviço feito, falta a loja" />
                    <Cartao rotulo="Cancelados" valor={cartoes.cancelados} cor="#64748b" />
                </div>
            </div>

            {/* ============ 2. POR LOJA ============ */}
            <div style={caixa}>
                <p style={titulo}>Por loja</p>
                <p style={subtitulo}>
                    Ordenado por volume. <strong>A loja sem chamado aparece com zero</strong> — some
                    da lista e a loja que não usa o portal viraria a loja sem problema.
                </p>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
                        <thead>
                            <tr>
                                <th style={th}>Loja</th>
                                <th style={{ ...th, textAlign: 'right' }}>Total</th>
                                <th style={{ ...th, textAlign: 'right' }}>Em aberto</th>
                                <th style={{ ...th, textAlign: 'right' }}>Em atraso</th>
                                <th style={{ ...th, textAlign: 'right' }}>Aguard. validação</th>
                                <th style={{ ...th, textAlign: 'right' }}>Cancelados</th>
                                <th style={{ ...th, textAlign: 'right' }}>% no prazo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {porLoja.map((l) => (
                                <tr key={l.loja}>
                                    <td style={{ ...td, fontWeight: 700, color: '#0f172a' }}>{l.loja}</td>
                                    <td style={{ ...td, textAlign: 'right' }}>{l.total}</td>
                                    <td style={{ ...td, textAlign: 'right' }}>{l.emAberto}</td>
                                    {/* ⚠️ O atraso é o único número colorido da linha: em
                                        vermelho só quando existe. Pintar o zero de
                                        vermelho claro faria a tabela inteira parecer
                                        alarme. */}
                                    <td style={{
                                        ...td, textAlign: 'right',
                                        color: l.emAtraso ? '#dc2626' : '#94a3b8',
                                        fontWeight: l.emAtraso ? 800 : 400,
                                    }}>
                                        {l.emAtraso}
                                    </td>
                                    <td style={{ ...td, textAlign: 'right' }}>{l.aguardandoValidacao}</td>
                                    <td style={{ ...td, textAlign: 'right' }}>{l.cancelados}</td>
                                    {/* ⚠️⚠️ TRAVESSÃO, NUNCA 0%, quando não há base: "0% no
                                        prazo" faria a loja sem chamado nenhum liderar a
                                        lista das piores. */}
                                    <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>
                                        {l.percentualNoPrazo == null ? '—' : `${l.percentualNoPrazo}%`}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ============ 3. O QUE MAIS QUEBRA ============ */}
            <div style={caixa}>
                <p style={titulo}>O que mais quebra</p>
                <p style={subtitulo}>
                    Equipamento × loja, do que mais gerou chamado.
                    {' '}<strong>“Sem equipamento” é uma linha, não uma exclusão</strong> — ela mede
                    quanto da fila ainda entra sem a etiqueta.
                </p>
                {dadosRanking.length === 0 ? (
                    <p style={{ margin: 0, fontSize: 13, color: '#94a3b8' }}>
                        Nenhum chamado no período escolhido.
                    </p>
                ) : (
                    <div style={{ width: '100%', height: Math.max(160, dadosRanking.length * 34) }}>
                        <ResponsiveContainer>
                            <BarChart
                                data={dadosRanking}
                                layout="vertical"
                                margin={{ top: 4, right: 40, bottom: 4, left: 4 }}
                            >
                                <XAxis type="number" hide />
                                {/* ⚠️ Nome de família não cabe deitado num eixo X — daí a
                                    barra HORIZONTAL, com o rótulo à esquerda e legível. */}
                                <YAxis
                                    type="category"
                                    dataKey="nome"
                                    width={ehMobile ? 130 : 230}
                                    tick={{ fontSize: 12, fill: '#475569' }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <Tooltip
                                    cursor={{ fill: '#f8fafc' }}
                                    formatter={(v) => [`${v} chamado(s)`, '']}
                                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0' }}
                                />
                                <Bar dataKey="chamados" radius={[0, 4, 4, 0]} barSize={16}>
                                    {dadosRanking.map((d) => (
                                        // ⚠️ Série ÚNICA: uma cor só (a do módulo). O cinza
                                        //    marca a linha "sem equipamento", que não é um
                                        //    equipamento — é a ausência dele.
                                        <Cell key={d.nome} fill={d.semEquipamento ? '#94a3b8' : COR} />
                                    ))}
                                    {/* Rótulo direto na ponta: série única não precisa de
                                        legenda, e o número ao lado evita a ida ao eixo. */}
                                    <LabelList
                                        dataKey="chamados"
                                        position="right"
                                        style={{ fontSize: 12, fontWeight: 700, fill: '#475569' }}
                                    />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            {/* ============ 4. PARA ONDE O ATENDIMENTO ESCAPA ============ */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ ...caixa, flex: '1 1 320px' }}>
                    <p style={titulo}>Distribuição de prioridade</p>
                    <p style={subtitulo}>
                        Quem define a prioridade é a triagem — nunca a loja.
                        {' '}<strong>Sem prioridade</strong> é chamado que ninguém triou ainda.
                    </p>
                    {totalPrioridade === 0 ? (
                        <p style={{ margin: 0, fontSize: 13, color: '#94a3b8' }}>Sem chamados no período.</p>
                    ) : (
                        <>
                            {/* A barra empilhada: composição de um todo, com 2px de
                                respiro entre as fatias (o fundo aparecendo é o que
                                separa uma da outra sem borda). */}
                            <div style={{ display: 'flex', gap: 2, height: 26, marginBottom: 12 }}>
                                {Object.entries(porPrioridade).map(([chave, valor]) => (
                                    valor > 0 && (
                                        <div
                                            key={chave}
                                            title={`${ROTULO_PRIORIDADE[chave]}: ${valor}`}
                                            style={{
                                                width: `${(valor / totalPrioridade) * 100}%`,
                                                backgroundColor: COR_PRIORIDADE[chave],
                                                borderRadius: 4,
                                            }}
                                        />
                                    )
                                ))}
                            </div>
                            {/* ⚠️ A legenda traz o NÚMERO junto — é o rótulo visível que o
                                tom mais claro da rampa exige (ele fica abaixo de 3:1 contra
                                o fundo, e sozinho seria invisível para quem enxerga pouco
                                contraste). */}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                                {Object.entries(porPrioridade).map(([chave, valor]) => (
                                    <span key={chave} style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                        fontSize: 12.5, color: '#475569',
                                    }}>
                                        <span style={{
                                            width: 10, height: 10, borderRadius: 3,
                                            backgroundColor: COR_PRIORIDADE[chave], flexShrink: 0,
                                        }} />
                                        {ROTULO_PRIORIDADE[chave]}
                                        <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</strong>
                                    </span>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                <div style={{ ...caixa, flex: '1 1 320px' }}>
                    <p style={titulo}>Esperando um terceiro</p>
                    <p style={subtitulo}>
                        Atendimentos finalizados por motivo — o que saiu da mão do técnico e está
                        com outro.
                    </p>
                    {totalMotivos === 0 ? (
                        <p style={{ margin: 0, fontSize: 13, color: '#94a3b8' }}>
                            Nenhum atendimento finalizado esperando terceiro no período.
                        </p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                            {Object.entries(porMotivoFinalizacao).map(([chave, valor]) => (
                                <div key={chave}>
                                    <div style={{
                                        display: 'flex', justifyContent: 'space-between',
                                        fontSize: 12.5, color: '#475569', marginBottom: 4,
                                    }}>
                                        <span>{ROTULO_MOTIVO[chave] || chave}</span>
                                        <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{valor}</strong>
                                    </div>
                                    {/* Barra fina, série única, ancorada à esquerda. */}
                                    <div style={{ height: 8, backgroundColor: '#f1f5f9', borderRadius: 4 }}>
                                        <div style={{
                                            width: `${totalMotivos ? (valor / totalMotivos) * 100 : 0}%`,
                                            height: '100%', backgroundColor: COR, borderRadius: 4,
                                        }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default ChamadosPainelGestao;
